import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import JSZip from 'jszip';
import mammoth from 'mammoth';

interface PreviewFile {
  file: File;
  preview?: SafeResourceUrl;
  textContent?: string;
  type?: string; // 'pdf', 'image', 'txt', 'docx', 'pptx'
}

@Component({
  selector: 'dga-file-uploader',
  templateUrl: './file-uploader.component.html',
  standalone: false,
})
export class FileUploaderComponent implements OnInit {
  @Output() htmlelement = new EventEmitter<HTMLInputElement>();
  @ViewChild('calificationFile') fileInput?: ElementRef<HTMLInputElement>;
  @Input() label: string = 'Upload file';
  @Input() grabLabel: string = 'or grab here';
  @Input() multi: boolean = false;
  @Input() accept: string = '.pdf,.png,.jpg,.jpeg,.txt,.docx,.pptx';
  @Output() public files = new EventEmitter<File[]>();

  localFiles: PreviewFile[] = [];
  fileIcon: string =
    '<svg height="1.2em" width="1.2em" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><g transform="matrix(4.8,0,0,4.8,0,0)"><path d="M8.558,0H1.442A1.434,1.434,0,0,0,0,1.418V8.582A1.434,1.434,0,0,0,1.442,10H5.25a.751.751,0,0,0,.531-.22l4-4A.75.75,0,0,0,10,5.25V1.418A1.434,1.434,0,0,0,8.558,0ZM1.444,1.745a.251.251,0,0,1,.248-.252l6.555-.072a.249.249,0,0,1,.253.25V4.5a.25.25,0,0,1-.25.25h-3a.5.5,0,0,0-.5.5V8.27a.25.25,0,0,1-.245.25l-2.752.057a.249.249,0,0,1-.179-.071A.246.246,0,0,1,1.5,8.329Z" fill="#000000"></path></g></svg>';

  constructor(private cd: ChangeDetectorRef, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.localFiles = [];
  }

  async addFiles(evt: Event): Promise<void> {
    const input = evt.target as HTMLInputElement;
    if (input.files && input.files.length) {
      for (const file of Array.from(input.files)) {
        const newFile: PreviewFile = { file };
        if (this.isImage(file)) {
          newFile.type = 'image';
          newFile.preview = await this.readAsDataURL(file);
        } else if (file.type === 'application/pdf') {
          newFile.type = 'pdf';
          newFile.preview = this.createObjectURL(file);
        } else if (file.type === 'text/plain') {
          newFile.type = 'txt';
          newFile.textContent = await this.readAsText(file);
        } else if (this.isDocx(file)) {
          newFile.type = 'docx';
          newFile.textContent = await this.readDocx(file);
        } else if (this.isDoc(file)) {
          newFile.type = 'doc';
          newFile.textContent = 'Vista previa no disponible para DOC.';
        }
        if (this.multi) {
          this.localFiles.push(newFile);
        } else {
          this.localFiles = [newFile];
        }
      }

      this.files.emit(this.localFiles.map(f => f.file));

      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
        this.htmlelement.emit(this.fileInput.nativeElement);
      }

      this.cd.detectChanges();
    }
  }

  removeFile(i: number): void {
    const removed = this.localFiles[i];
    if (removed?.type === 'pdf' && removed.preview) {
      const objectUrl = (removed.preview as unknown as string);
      URL.revokeObjectURL(objectUrl);
    }

    this.localFiles.splice(i, 1);
    this.files.emit(this.localFiles.map(f => f.file));
    this.cd.detectChanges();
  }

  clearFiles(): void {
    this.localFiles.forEach(f => {
      if (f.type === 'pdf' && f.preview) {
        const objectUrl = (f.preview as unknown as string);
        URL.revokeObjectURL(objectUrl);
      }
    });
    this.localFiles = [];
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    this.files.emit([]);
    this.cd.detectChanges();
  }

  private async readAsDataURL(file: File): Promise<SafeResourceUrl> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(this.sanitizer.bypassSecurityTrustResourceUrl(reader.result as string));
      reader.readAsDataURL(file);
    });
  }

  private async readAsText(file: File): Promise<string> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsText(file);
    });
  }

  private createObjectURL(file: File): SafeResourceUrl {
    const blobUrl = URL.createObjectURL(file);
    return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
  }

  isImage(file: File): boolean {
    return ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type);
  }

  isDocx(file: File): boolean {
    return file.name.toLowerCase().endsWith('.docx');
  }

  isDoc(file: File): boolean {
    return file.name.toLowerCase().endsWith('.doc');
  }

  isPptx(file: File): boolean {
    return file.name.toLowerCase().endsWith('.pptx');
  }

  /** WORD */
  private async readDocx(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  }
}

